<template>
  <div class="filter-wrapper">
    <div class="title mb-2">Filtrar resultados</div>

    <div class="principal">
      <Dropdown
        v-model="field"
        :options="fieldsOptions"
        optionLabel="name"
        class="mr-2 p-inputtext-sm field"
        placeholder="Filtro"
        :disabled="isLoading"
      />

      <Calendar
        v-if="field.field === 'fecha'"
        v-model="draftFecha"
        dateFormat="dd/mm/yy"
        class="p-inputtext-sm field calendar-field"
        inputClass="w-full"
        placeholder="Seleccione fecha"
        :manualInput="false"
        showIcon
        :disabled="isLoading"
      />

      <div v-else-if="field.field === 'rango'" class="range-fields field-range">
        <Calendar
          v-model="draftFechaDesde"
          dateFormat="dd/mm/yy"
          class="p-inputtext-sm field calendar-field"
          inputClass="w-full"
          placeholder="Desde"
          :manualInput="false"
          :disabled="isLoading"
        />
        <Calendar
          v-model="draftFechaHasta"
          dateFormat="dd/mm/yy"
          class="p-inputtext-sm field calendar-field"
          inputClass="w-full"
          placeholder="Hasta"
          :manualInput="false"
          :disabled="isLoading"
        />
      </div>

      <InputText
        v-else-if="field.field === 'identificacion'"
        v-model="draftIdentificacion"
        placeholder="Buscar identificación"
        class="p-inputtext-sm field"
        :disabled="isLoading"
      />

      <Dropdown
        v-else
        v-model="draftDepartamental"
        :options="departamentalOptions"
        optionLabel="label"
        optionValue="value"
        class="p-inputtext-sm field"
        placeholder="Seleccionar departamental"
        :disabled="isLoading"
      />

      <Button
        class="search-button flex-grow-0"
        @click="handleSearch"
        :disabled="isLoading || !hasDraftValue"
      >
        <div>
          <Search color="white" :size="20" />
        </div>
        Buscar
      </Button>

      <Button
        v-if="hasAnyActiveFilter"
        class="clean-button flex-grow-0"
        @click="handleClear"
        :disabled="isLoading"
      >
        <div>
          <BrushCleaning color="#0B5195" :size="20" />
        </div>
        Limpiar
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { Search, BrushCleaning } from "lucide-vue";

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
  departamentalOptions: {
    type: Array,
    default: () => [],
  },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(["apply", "clear"]);

const fieldsOptions = ref([
  { name: "Fecha", field: "fecha" },
  { name: "Rango de fecha", field: "rango" },
  { name: "Identificación", field: "identificacion" },
  { name: "Departamental", field: "departamental" },
]);

const field = ref(fieldsOptions.value[0]);

const formatForModel = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return value;
};

const parseDate = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value).split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const localFilters = reactive({
  fecha: parseDate(props.filters.fecha),
  fechaDesde: parseDate(props.filters.fechaDesde),
  fechaHasta: parseDate(props.filters.fechaHasta),
  identificacion: props.filters.identificacion || "",
  departamental: props.filters.departamental || null,
});

const draftFecha = ref(parseDate(props.filters.fecha));
const draftFechaDesde = ref(parseDate(props.filters.fechaDesde));
const draftFechaHasta = ref(parseDate(props.filters.fechaHasta));
const draftIdentificacion = ref(props.filters.identificacion || "");
const draftDepartamental = ref(props.filters.departamental || null);

const syncDraftWithSelectedField = () => {
  if (field.value.field === "fecha") {
    draftFecha.value = localFilters.fecha;
    return;
  }

  if (field.value.field === "rango") {
    draftFechaDesde.value = localFilters.fechaDesde;
    draftFechaHasta.value = localFilters.fechaHasta;
    return;
  }

  if (field.value.field === "identificacion") {
    draftIdentificacion.value = localFilters.identificacion;
    return;
  }

  draftDepartamental.value = localFilters.departamental;
};

watch(
  () => props.filters,
  (nextFilters) => {
    localFilters.fecha = parseDate(nextFilters.fecha);
    localFilters.fechaDesde = parseDate(nextFilters.fechaDesde);
    localFilters.fechaHasta = parseDate(nextFilters.fechaHasta);
    localFilters.identificacion = nextFilters.identificacion || "";
    localFilters.departamental = nextFilters.departamental || null;

    syncDraftWithSelectedField();
  },
  { deep: true }
);

watch(
  () => field.value,
  () => {
    syncDraftWithSelectedField();
  }
);

const hasDraftValue = computed(() => {
  if (field.value.field === "fecha") {
    return Boolean(draftFecha.value);
  }

  if (field.value.field === "rango") {
    return Boolean(draftFechaDesde.value || draftFechaHasta.value);
  }

  if (field.value.field === "identificacion") {
    return Boolean(String(draftIdentificacion.value || "").trim());
  }

  return Boolean(draftDepartamental.value);
});

const hasAnyActiveFilter = computed(() => {
  return Boolean(
    localFilters.fecha ||
      localFilters.fechaDesde ||
      localFilters.fechaHasta ||
      String(localFilters.identificacion || "").trim() ||
      localFilters.departamental
  );
});

const emitFilters = () => {
  emit("apply", {
    fecha: formatForModel(localFilters.fecha),
    fechaDesde: formatForModel(localFilters.fechaDesde),
    fechaHasta: formatForModel(localFilters.fechaHasta),
    identificacion: String(localFilters.identificacion || "").trim(),
    departamental: localFilters.departamental || null,
  });
};

const handleSearch = () => {
  if (field.value.field === "fecha") {
    localFilters.fecha = draftFecha.value;
  }

  if (field.value.field === "rango") {
    localFilters.fechaDesde = draftFechaDesde.value;
    localFilters.fechaHasta = draftFechaHasta.value;
  }

  if (field.value.field === "identificacion") {
    localFilters.identificacion = String(draftIdentificacion.value || "").trim();
  }

  if (field.value.field === "departamental") {
    localFilters.departamental = draftDepartamental.value || null;
  }

  emitFilters();
};

const handleClear = () => {
  localFilters.fecha = null;
  localFilters.fechaDesde = null;
  localFilters.fechaHasta = null;
  localFilters.identificacion = "";
  localFilters.departamental = null;
  draftFecha.value = null;
  draftFechaDesde.value = null;
  draftFechaHasta.value = null;
  draftIdentificacion.value = "";
  draftDepartamental.value = null;
  emit("clear");
};
</script>

<style scoped>
.filter-wrapper {
  flex: 1;
}

.principal {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.title {
  font-weight: 600;
  font-size: 0.8em;
}

.field {
  min-width: 200px;
}

.range-fields {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.field-range {
  min-width: 420px;
}

.search-button {
  height: 35px;
  gap: 10px;
  border-radius: 6px;
  background: #0b5195;
  color: #ffffff;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
}

.search-button:hover {
  background: #0b5195 !important;
  color: #f4f4f4 !important;
  opacity: 0.9;
  cursor: pointer;
}

.clean-button {
  height: 35px;
  gap: 10px;
  border-radius: 6px;
  background: var(--White-White, #fff);
  color: #0b5195;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
}

.clean-button:hover {
  background: #f4f4f4 !important;
  color: #0b5195 !important;
  opacity: 0.9;
  cursor: pointer;
}

.principal :deep(.p-inputtext),
.principal :deep(.p-dropdown),
.principal :deep(.p-calendar) {
  border-radius: 6px !important;
}

.calendar-field :deep(.p-inputtext) {
  min-height: 35px;
  border: 1px solid #ced4da;
  border-right: none;
  border-radius: 6px 0 0 6px !important;
  box-shadow: none;
}

.calendar-field :deep(.p-datepicker-trigger) {
  height: 35px;
  min-width: 35px;
  border-radius: 0 6px 6px 0 !important;
  background: #0b5195;
  border: 1px solid #0b5195;
}

.calendar-field :deep(.p-datepicker-trigger:hover) {
  background: #0b5095c5 !important;
  border-color: #0b5095c5 !important;
}

.calendar-field :deep(.p-inputtext:enabled:focus) {
  box-shadow: none;
  border-color: #0b5195;
}

.calendar-field :deep(.p-calendar) {
  display: flex;
  align-items: center;
}
</style>
